import { OptimizationResults, OptimizationResultsWithStats } from '../types';
import { RESULT_ALGORITHM_KEYS, RESULT_ALGORITHM_NAMES } from './algorithmRegistry';

export interface CombinedWinnerResult {
  perfectWinners: Array<{
    name: string;
    key: string;
    fitness: number;
    energy: number;
    fitnessRank: number;
    energyRank: number;
  }>;
  bestAlternative: {
    name: string;
    key: string;
    fitness: number;
    energy: number;
    fitnessRank: number;
    energyRank: number;
  } | null;
  rankedAlgorithms: Array<{
    name: string;
    key: string;
    fitness: number;
    energy: number;
    fitnessRank: number;
    energyRank: number;
    isPerfectWinner: boolean;
    isBestAlternative: boolean;
  }>;
  hasPerfectWinner: boolean;
  bestFitness: number;
  bestEnergy: number;
}

/**
 * Calculates combined winner using:
 * - Primary: lowest median energy
 * - Secondary: highest median composite score (when available)
 * - Tertiary: highest median fitness
 * - Stability tie-breakers: lowest standard deviation
 * 
 * If statistics are available (multiple runs), uses MEDIAN values across all runs
 * Otherwise, uses the single run's values
 */
export function calculateCombinedWinner(results: OptimizationResults | OptimizationResultsWithStats): CombinedWinnerResult | null {
  const algorithmKeys = RESULT_ALGORITHM_KEYS;
  const algorithmNames = algorithmKeys.map((key) => RESULT_ALGORITHM_NAMES[key]);

  // Check if statistics are available (multiple runs were performed)
  const hasStatistics = 'statistics' in results && results.statistics !== undefined;
  const hasCompositeScores = 'compositeScores' in results && results.compositeScores !== undefined;

  // Get all algorithms with results
  // If statistics available, use MEDIAN values across all runs; otherwise use single run values
  const allAlgorithms = algorithmKeys
    .map((key, index) => {
      const result = results[key];
      if (!result) return null;
      
      let fitness: number;
      let energy: number;
      let fitnessStdDev: number = 0;
      let energyStdDev: number = 0;
      let compositeScore: number | null = null;
      let compositeScoreStdDev: number = 0;
      
      if (hasStatistics && results.statistics) {
        const stats = results.statistics[key];
        if (!stats) {
          return null;
        }
        // Use MEDIAN values from statistics (across all runs) - more robust than mean
        fitness = stats.fitness.median;
        energy = stats.energy.median;
        fitnessStdDev = stats.fitness.stdDev;
        energyStdDev = stats.energy.stdDev;
      } else {
        // Use single run values
        fitness = result.fitness;
        energy = result.energy;
      }
      
      // Get composite score if available
      if (hasCompositeScores && results.compositeScores && results.compositeScores[key]) {
        compositeScore = results.compositeScores[key].median;
        compositeScoreStdDev = results.compositeScores[key].stdDev;
      }
      
      return {
        name: algorithmNames[index],
        key: algorithmKeys[index],
        fitness,
        energy,
        fitnessStdDev,
        energyStdDev,
        compositeScore,
        compositeScoreStdDev,
      };
    })
    .filter((entry): entry is NonNullable<typeof entry> => entry !== null);

  if (allAlgorithms.length === 0) return null;

  // Helper function to compare floating point numbers with epsilon tolerance
  const isEqual = (a: number, b: number, epsilon = 1e-9): boolean => {
    return Math.abs(a - b) < epsilon;
  };

  const compareCompositeSecondary = (
    a: (typeof allAlgorithms)[number],
    b: (typeof allAlgorithms)[number]
  ): number => {
    const aHasComposite = a.compositeScore !== null;
    const bHasComposite = b.compositeScore !== null;

    if (aHasComposite && bHasComposite && !isEqual(a.compositeScore!, b.compositeScore!)) {
      return b.compositeScore! - a.compositeScore!;
    }
    if (aHasComposite && !bHasComposite) return -1;
    if (!aHasComposite && bHasComposite) return 1;

    if (!isEqual(a.fitness, b.fitness)) {
      return b.fitness - a.fitness;
    }
    if (!isEqual(a.energyStdDev, b.energyStdDev)) {
      return a.energyStdDev - b.energyStdDev;
    }
    if (!isEqual(a.fitnessStdDev, b.fitnessStdDev)) {
      return a.fitnessStdDev - b.fitnessStdDev;
    }
    if (
      aHasComposite &&
      bHasComposite &&
      !isEqual(a.compositeScoreStdDev, b.compositeScoreStdDev)
    ) {
      return a.compositeScoreStdDev - b.compositeScoreStdDev;
    }

    return a.name.localeCompare(b.name);
  };

  // Sort by fitness (descending - higher is better) and energy (ascending - lower is better)
  const fitnessSorted = [...allAlgorithms].sort((a, b) => {
    // Primary: fitness (descending)
    if (Math.abs(a.fitness - b.fitness) > 1e-9) {
      return b.fitness - a.fitness;
    }
    // Tie-breaker: lower stdDev (more stable)
    return a.fitnessStdDev - b.fitnessStdDev;
  });
  
  const energySorted = [...allAlgorithms].sort((a, b) => {
    // Primary: energy (ascending - lower is better)
    if (Math.abs(a.energy - b.energy) > 1e-9) {
      return a.energy - b.energy;
    }
    // Tie-breaker: lower stdDev (more stable)
    return a.energyStdDev - b.energyStdDev;
  });

  // Find algorithms that are #1 in BOTH fitness AND energy (using median)
  const bestFitness = fitnessSorted[0].fitness;
  const bestEnergy = energySorted[0].energy;

  // Find the algorithm(s) that are #1 in energy (using floating point comparison)
  const energyWinners = allAlgorithms.filter((alg) => isEqual(alg.energy, bestEnergy));

  // Check if there's an algorithm that wins BOTH fitness AND energy
  const perfectWinners = allAlgorithms.filter(
    (alg) => isEqual(alg.fitness, bestFitness) && isEqual(alg.energy, bestEnergy)
  );

  // If no perfect winner, find the best alternative using:
  // 1. Primary: lowest median energy
  // 2. Secondary: highest median composite score (if available)
  // 3. Tertiary: highest median fitness
  // 4. Stability: lowest standard deviation
  let bestAlternative: typeof allAlgorithms[0] | null = null;

  if (perfectWinners.length === 0) {
    bestAlternative = [...energyWinners].sort(compareCompositeSecondary)[0];
  }

  const energyFocusedSorted = [...allAlgorithms].sort((a, b) => {
    if (!isEqual(a.energy, b.energy)) {
      return a.energy - b.energy;
    }
    return compareCompositeSecondary(a, b);
  });

  const rankedAlgorithmsInternal = energyFocusedSorted.map((alg) => {
    const fitnessRank = fitnessSorted.findIndex((a) => a.key === alg.key) + 1;
    const energyRank = energySorted.findIndex((a) => a.key === alg.key) + 1;

    const isPerfectWinner = isEqual(alg.fitness, bestFitness) && isEqual(alg.energy, bestEnergy);
    const isBestAlternative = bestAlternative && alg.key === bestAlternative.key;

    return {
      ...alg,
      fitnessRank,
      energyRank,
      isPerfectWinner,
      isBestAlternative: !!isBestAlternative,
    };
  });

  // Sort: perfect winners first, then best alternative, then by energy rank,
  // composite score / fitness, and finally stability.
  rankedAlgorithmsInternal.sort((a, b) => {
    if (a.isPerfectWinner && !b.isPerfectWinner) return -1;
    if (!a.isPerfectWinner && b.isPerfectWinner) return 1;
    if (a.isBestAlternative && !b.isBestAlternative) return -1;
    if (!a.isBestAlternative && b.isBestAlternative) return 1;
    if (a.energyRank !== b.energyRank) return a.energyRank - b.energyRank;
    if (a.compositeScore !== null || b.compositeScore !== null) {
      const compositeComparison = compareCompositeSecondary(a, b);
      if (compositeComparison !== 0) return compositeComparison;
    }
    if (a.fitnessRank !== b.fitnessRank) return a.fitnessRank - b.fitnessRank;
    return a.name.localeCompare(b.name);
  });

  const rankedAlgorithms = rankedAlgorithmsInternal.map((alg) => ({
    name: alg.name,
    key: alg.key,
    fitness: alg.fitness,
    energy: alg.energy,
    fitnessRank: alg.fitnessRank,
    energyRank: alg.energyRank,
    isPerfectWinner: alg.isPerfectWinner,
    isBestAlternative: alg.isBestAlternative,
  }));

  return {
    perfectWinners: perfectWinners.map(winner => ({
      ...winner,
      fitnessRank: rankedAlgorithms.find(a => a.key === winner.key)?.fitnessRank || 1,
      energyRank: rankedAlgorithms.find(a => a.key === winner.key)?.energyRank || 1,
    })),
    bestAlternative: bestAlternative ? {
      ...bestAlternative,
      fitnessRank: rankedAlgorithms.find(a => a.key === bestAlternative!.key)?.fitnessRank || 1,
      energyRank: rankedAlgorithms.find(a => a.key === bestAlternative!.key)?.energyRank || 1,
    } : null,
    rankedAlgorithms,
    hasPerfectWinner: perfectWinners.length > 0,
    bestFitness,
    bestEnergy,
  };
}
